import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Palette, Package, Truck, ShieldCheck, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { useSettings } from "../lib/SettingsContext";
import { http, API } from "../lib/api";
import HomepageCarousel from "../components/HomepageCarousel";
import HomepageVideo from "../components/HomepageVideo";

const ICON_MAP = { palette: Palette, package: Package, truck: Truck, shield: ShieldCheck, star: Star };

const CATEGORY_FALLBACK_IMG = {
  playera: "https://images.unsplash.com/photo-1523634450041-0d0fbceb4036?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  taza: "https://images.unsplash.com/photo-1643946404043-178456b0e3f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  termo: "https://images.unsplash.com/photo-1664714628878-9d2aa898b9e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  camisa: "https://images.unsplash.com/photo-1603252109612-24fa03d145c8?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  lanyard: "https://images.unsplash.com/photo-1591901672731-1cf74812fb84?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
};

export default function LandingPage() {
  const { settings } = useSettings();
  const accent = settings?.brand_accent || "#FF3B30";
  const [categories, setCategories] = useState([]);
  const [productCount, setProductCount] = useState(null);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    Promise.all([http.get("/categories"), http.get("/products")])
      .then(([c, p]) => {
        setProductCount(p.data.length);
        const byCat = {};
        for (const prod of p.data) {
          if (!byCat[prod.category]) {
            const firstImg = (prod.images && prod.images[0]) || prod.base_image_url;
            byCat[prod.category] = firstImg?.startsWith("http")
              ? firstImg
              : firstImg
                ? `${API}/files/${firstImg}`
                : null;
          }
        }
        const enriched = c.data.map((cat) => {
          const raw = cat.image_url;
          const catImg = raw ? (raw.startsWith("http") ? raw : `${API}/files/${raw}`) : null;
          return {
            ...cat,
            tile_image:
              catImg ||
              byCat[cat.slug] ||
              CATEGORY_FALLBACK_IMG[cat.slug] ||
              "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=600",
          };
        });
        setCategories(enriched);
        // Featured product picker
        const featuredId = settings?.featured_product_id;
        if (featuredId) {
          const fp = p.data.find((x) => x.product_id === featuredId && x.active !== false);
          setFeatured(fp || null);
        } else {
          setFeatured(null);
        }
      })
      .catch(() => setCategories([]));
  }, [settings?.featured_product_id]);

  const features = (settings?.features && settings.features.length > 0) ? settings.features : [
    { icon: "palette", title: "Mockup en vivo", copy: "Visualiza tu diseño sobre el producto antes de comprar." },
    { icon: "package", title: "Producción propia", copy: "DTF, sublimación y grabado láser en nuestro estudio." },
    { icon: "truck", title: "Entrega nacional", copy: "Envío a todo México con seguimiento en tiempo real." },
    { icon: "shield", title: "Garantía", copy: "Si algo no sale como pediste, lo reimprimimos gratis." },
  ];

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="text-xs font-mono uppercase tracking-widest mb-6" style={{ color: accent }} data-testid="hero-tagline">
              /01 {settings.hero_tagline}
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter" data-testid="hero-heading">
              {settings.hero_line_1}<br />
              {settings.hero_line_2}<br />
              <span style={{ color: accent }}>{settings.hero_line_3}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-neutral-600 max-w-xl">
              {settings.hero_subheadline}
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Button asChild size="lg" className="brutal-btn bg-black hover:bg-[#FF3B30] text-white h-14 px-8" data-testid="hero-shop-btn">
                <Link to="/tienda">
                  {settings.hero_cta_text || "Explorar productos"} <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="brutal-btn h-14 px-8 border-black" data-testid="hero-how-btn">
                <Link to="/como-funciona">Cómo funciona</Link>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 gap-3">
            <div className="brutal-card p-4">
              <div className="font-mono text-xs uppercase text-neutral-500">Productos activos</div>
              <div className="font-display text-4xl mt-2" data-testid="hero-product-count">
                {productCount !== null ? `${productCount}+` : "—"}
              </div>
            </div>
            <div className="brutal-card p-4">
              <div className="font-mono text-xs uppercase text-neutral-500">Entrega</div>
              <div className="font-display text-4xl mt-2">5-12<span className="text-lg text-neutral-500">d</span></div>
            </div>
            <div className="brutal-card p-4 col-span-2 bg-black text-white">
              <div className="font-mono text-xs uppercase text-neutral-400">Envío gratis</div>
              <div className="font-display text-3xl mt-2">Desde $800 MXN</div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive homepage carousel — admin manages slides via Ajustes */}
      <HomepageCarousel slides={settings?.homepage_carousel || []} accent={accent} />

      {/* Featured product — configurable via Admin > Ajustes > Producto destacado */}
      {featured && (
        <section className="border-b border-neutral-200 bg-neutral-50" data-testid="landing-featured-product">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-10 items-center">
            <Link
              to={`/producto/${featured.product_id}`}
              className="brutal-card overflow-hidden aspect-[4/5] group"
              data-testid="landing-featured-image"
            >
              <img
                src={
                  ((featured.images && featured.images[0]) || featured.base_image_url || "").startsWith("http")
                    ? (featured.images?.[0] || featured.base_image_url)
                    : `${API}/files/${featured.images?.[0] || featured.base_image_url}`
                }
                alt={featured.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </Link>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest mb-4" style={{ color: accent }}>
                /destacado
              </div>
              <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95]" data-testid="landing-featured-name">
                {featured.name}
              </h2>
              <p className="mt-4 text-neutral-600 text-base md:text-lg max-w-lg">
                {featured.description}
              </p>
              <div className="mt-6 flex items-baseline gap-4 flex-wrap">
                <div className="font-display text-4xl" data-testid="landing-featured-price">
                  ${Number(featured.base_price).toFixed(2)}
                </div>
                <div className="text-xs font-mono uppercase text-neutral-500">MXN · desde</div>
                {featured.min_qty && featured.min_qty > 1 && (
                  <div className="text-xs px-2 py-0.5 border border-[#FF3B30] text-[#FF3B30]">
                    Pedido mínimo {featured.min_qty} pzs
                  </div>
                )}
              </div>
              <div className="mt-6 flex gap-3 flex-wrap">
                <Button
                  asChild
                  size="lg"
                  className="brutal-btn bg-black hover:bg-[#FF3B30] text-white h-12 px-6"
                  data-testid="landing-featured-cta"
                >
                  <Link to={`/producto/${featured.product_id}`}>
                    Personalizar ahora <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Category grid - dynamic */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">/02 Catálogo</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tighter">Encuentra tu lienzo.</h2>
          </div>
          <Link to="/tienda" className="hidden sm:flex items-center gap-1 text-sm font-medium hover:text-[#FF3B30]" data-testid="landing-view-all">
            Ver todo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {categories.length === 0 ? (
          <div className="brutal-card p-12 text-center text-neutral-500" data-testid="landing-no-categories">
            Sin categorías configuradas. Crea una desde el panel administrador.
          </div>
        ) : (
          <div
            className={`grid gap-4 ${
              categories.length === 1
                ? "grid-cols-1 max-w-md"
                : categories.length === 2
                ? "grid-cols-1 sm:grid-cols-2"
                : categories.length === 3
                ? "grid-cols-2 md:grid-cols-3"
                : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            }`}
            data-testid="landing-categories-grid"
          >
            {categories.map((c) => (
              <Link
                key={c.category_id || c.slug}
                to={`/tienda?cat=${c.slug}`}
                className="brutal-card group relative aspect-[4/5] overflow-hidden hover:-translate-y-1 transition-transform"
                data-testid={`landing-category-${c.slug}`}
              >
                <img src={c.tile_image} alt={c.name} className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                  <div className="font-display text-2xl text-white tracking-tighter">{c.name}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Features */}
      <section className="border-y border-neutral-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-6">
          {features.map((f, i) => {
            const Icon = ICON_MAP[f.icon] || Star;
            return (
              <div key={i} className="border-l border-neutral-200 pl-4 first:border-l-0 first:pl-0">
                <Icon className="w-6 h-6" style={{ color: accent }} />
                <div className="font-display text-lg mt-3 tracking-tight">{f.title}</div>
                <p className="text-sm text-neutral-600 mt-1">{f.copy}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Videos */}
      {(settings?.homepage_videos || []).filter((v) => v.enabled && v.url).length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20" data-testid="landing-videos-section">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">/03 Video</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter">Mira cómo lo hacemos.</h2>
            </div>
          </div>
          <div className={`grid gap-6 ${
            (settings?.homepage_videos || []).filter((v) => v.enabled && v.url).length === 1
              ? "grid-cols-1 max-w-3xl"
              : "grid-cols-1 md:grid-cols-2"
          }`}>
            {(settings?.homepage_videos || [])
              .filter((v) => v.enabled && v.url)
              .map((v, i) => (
                <HomepageVideo key={i} url={v.url} title={v.title} subtitle={v.subtitle} />
              ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="brutal-card bg-black text-white p-10 md:p-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-mono text-xs uppercase text-neutral-400 mb-3">/03 Empieza ahora</div>
            <h3 className="font-display text-3xl md:text-5xl tracking-tighter">
              Tu diseño impreso.<br />En 5 días o menos.
            </h3>
          </div>
          <Button asChild size="lg" className="brutal-btn text-white h-14 px-8 hover:bg-white hover:text-black" style={{ background: accent }} data-testid="landing-cta-btn">
            <Link to="/tienda">Configurar pedido <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
